/**
 * A turn's work, folded under its reply (S-509): the line that says how long
 * it worked and how many steps, opening into them, inline under the reply
 * from `md` up and in a sheet on a phone. The steps come when it first opens;
 * their copy is `worker/domain/step-copy.ts`, shared with the Worker's count.
 */
import { Collapsible, CollapsibleContent } from "@crew/ui/components/collapsible";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from "@crew/ui/components/drawer";
import { Spinner } from "@crew/ui/components/spinner";
import {
  ComputerCard,
  foldMotion,
  WorkLine,
  WorkMember,
  WorkOutput,
  WorkPanel,
  WorkRow,
} from "@crew/ui/components/work-fold";
import {
  AlarmClock,
  Code,
  FileText,
  Globe,
  LayoutList,
  NotebookText,
  Plug,
  Search,
  SquareTerminal,
} from "lucide-react";
import { useState, type ReactNode } from "react";
import {
  breadcrumb,
  foldEntries,
  paramField,
  stepLabel,
  type FoldEntry,
} from "../../../worker/domain/step-copy";
import { useWide } from "../../app/wide";
import { Link } from "@crew/ui/components/link";
import { StatusDot } from "@crew/ui/components/status";
import { duration } from "../activity/activity-view";
import type { TaskItem } from "../tasks/api";
import { taskStatus } from "../tasks/task-view";
import { useTurnSteps, type Turn, type TurnStep } from "./api";
import { stepCount, workedMs, workSummary } from "./work";
import { FinishedPicture } from "./browser-frames";

const ICONS: Record<string, ReactNode> = {
  search: <Search strokeWidth={1.75} />,
  page: <Globe strokeWidth={1.75} />,
  file: <FileText strokeWidth={1.75} />,
  docs: <FileText strokeWidth={1.75} />,
  command: <SquareTerminal strokeWidth={1.75} />,
  tasks: <LayoutList strokeWidth={1.75} />,
  routines: <AlarmClock strokeWidth={1.75} />,
  note: <NotebookText strokeWidth={1.75} />,
};

function iconOf(step: TurnStep): ReactNode {
  if (step.tool === "codemode") return <Code strokeWidth={1.75} />;
  if (step.tool === "integrations_execute") return <Plug strokeWidth={1.75} />;
  if (step.tool.startsWith("memory_")) return ICONS.note;
  return ICONS[step.kind] ?? <Plug strokeWidth={1.75} />;
}

/** A step's time: "0.4s", "12s", never "0.0s". */
const short = (n: number) => duration(Math.max(n, 100), { precise: true });
const ms = (n: number | null) => (n === null ? undefined : short(n));

/** A command, and what it printed. */
function commandOutput(step: TurnStep): ReactNode {
  const command = paramField(step.params, "command") ?? step.detail;
  const tone = step.status === "error" ? "error" : undefined;
  return (
    <>
      <WorkOutput>{`$ ${command}`}</WorkOutput>
      {step.result ? <WorkOutput tone={tone}>{step.result}</WorkOutput> : null}
    </>
  );
}

/** A script, its own calls, and what it printed. */
function scriptOutput(step: TurnStep, children: TurnStep[]): ReactNode {
  const code = paramField(step.params, "code", "script");
  return (
    <>
      {code ? <WorkOutput>{code}</WorkOutput> : null}
      {children.map((c) => (
        <WorkMember key={c.call_id} label={stepLabel(c)} duration={ms(c.duration_ms)} />
      ))}
      {step.result ? <WorkOutput>{step.result}</WorkOutput> : null}
    </>
  );
}

/** What an opened step shows: a command and what it printed, a script, or what came back. */
function stepOutput(step: TurnStep, children: TurnStep[]): ReactNode {
  if (step.kind === "command") return commandOutput(step);
  if (step.tool === "codemode") return scriptOutput(step, children);
  if (step.result && step.kind !== "browser") return <WorkOutput>{step.result}</WorkOutput>;
  return null;
}

/** The turn's browser steps in one card, with the picture of its last page or the step pressed (slice 6f). */
function ComputerEntry({
  entry,
  threadId,
  turnId,
}: {
  entry: Extract<FoldEntry, { type: "computer" }>;
  threadId: string;
  turnId: string;
}) {
  const [selected, setSelected] = useState<string | null>(null);
  return (
    <ComputerCard
      meta={`${stepCount(entry.steps.length)} · ${short(entry.ms)}`}
      breadcrumb={breadcrumb(entry.sites)}
      picture={
        <FinishedPicture
          threadId={threadId}
          turnId={turnId}
          steps={entry.steps}
          selected={selected}
        />
      }
      selected={selected}
      onSelect={(key) => setSelected(key === selected ? null : key)}
      steps={entry.steps.map((s) => ({
        key: s.call_id,
        label: stepLabel(s),
        duration: ms(s.duration_ms),
        failed: s.status === "error",
        pictured: s.frame === "saved",
      }))}
    />
  );
}

function EntryView({
  entry,
  all,
  threadId,
  turnId,
}: {
  entry: FoldEntry;
  all: TurnStep[];
  threadId: string;
  turnId: string;
}) {
  const byId = (id: string) => all.find((s) => s.call_id === id) as TurnStep;
  if (entry.type === "computer") {
    return <ComputerEntry entry={entry} threadId={threadId} turnId={turnId} />;
  }
  if (entry.type === "group") {
    const first = byId(entry.steps[0].call_id);
    return (
      <WorkRow icon={iconOf(first)} label={entry.label} duration={short(entry.ms)}>
        {entry.steps.map((s) => (
          <WorkMember
            key={s.call_id}
            label={s.detail || stepLabel(s)}
            duration={ms(s.duration_ms)}
          />
        ))}
      </WorkRow>
    );
  }
  const step = byId(entry.step.call_id);
  const output = stepOutput(
    step,
    entry.children.map((c) => byId(c.call_id)),
  );
  return (
    <WorkRow
      icon={iconOf(step)}
      label={entry.label}
      detail={step.via}
      duration={ms(step.duration_ms)}
      failed={step.status === "error"}
      error={step.error}
    >
      {output ?? undefined}
    </WorkRow>
  );
}

/** The opened fold's rows, once the steps have come. */
function WorkSteps({
  threadId,
  turnId,
  open,
}: {
  threadId: string;
  turnId: string;
  open: boolean;
}) {
  const q = useTurnSteps(threadId, turnId, open);
  if (q.isPending) {
    return (
      <div className="flex min-h-11 items-center gap-2 pl-10 text-meta text-muted-foreground">
        <Spinner className="size-3.5" /> Loading the steps
      </div>
    );
  }
  if (q.isError) {
    return (
      <p className="min-h-11 pl-10 text-meta text-destructive">
        Couldn't load the steps. Close it and try again.
      </p>
    );
  }
  const entries = foldEntries(q.data);
  return (
    <WorkPanel>
      {entries.map((e) => (
        <EntryView
          key={
            e.type === "step"
              ? e.step.call_id
              : e.type === "group"
                ? `g:${e.steps[0].call_id}`
                : "computer"
          }
          entry={e}
          all={q.data}
          threadId={threadId}
          turnId={turnId}
        />
      ))}
    </WorkPanel>
  );
}

/** The turn's task as a status leading its line, to the task: "● Task · Done". */
function TaskLead({ task, viewerId }: { task: TaskItem; viewerId: string }) {
  const s = taskStatus(task, viewerId);
  return (
    <Link
      href={`/tasks/${task.id}`}
      data-turn-task={task.id}
      aria-label={`Task: ${task.title} · ${s.label}`}
      className="pressable -ml-2 flex min-h-11 shrink-0 items-center gap-1.5 rounded-nav px-2 text-meta font-medium text-foreground outline-none hover:underline focus-visible:ring-2 focus-visible:ring-ring/50 md:min-h-[30px]"
    >
      <StatusDot tone={s.tone} hollow={s.hollow} />
      Task · {s.label}
    </Link>
  );
}

/**
 * A finished turn's line: its own task as a status, leading it, then its
 * fold. With no steps, the status is the whole line.
 */
export function TurnWork({
  task,
  viewerId,
  ...fold
}: {
  turn: Turn;
  threadId: string;
  quiet: boolean;
  agentName: string;
  /** When it replied, for the sheet's header: "9:14". */
  time: string;
  /** The turn's task, said as a status instead of a card. */
  task?: TaskItem;
  viewerId: string;
}) {
  const lead = task ? <TaskLead task={task} viewerId={viewerId} /> : null;
  if (fold.turn.work?.steps) return <WorkFold {...fold} lead={lead} />;
  return lead ? (
    <div data-work={fold.turn.id} className="flex pl-10">
      {lead}
    </div>
  ) : null;
}

/**
 * A finished turn's fold. From `md` up it opens inline under the reply; on a
 * phone it always opens a sheet (S-509 decision 4), so the thread never moves.
 */
function WorkFold({
  turn,
  threadId,
  quiet,
  agentName,
  time,
  lead,
}: {
  turn: Turn;
  threadId: string;
  quiet: boolean;
  agentName: string;
  time: string;
  lead: ReactNode;
}) {
  const wide = useWide();
  const [open, setOpen] = useState(false);
  const failed = turn.work?.failed ?? 0;
  const line = (
    <>
      {lead}
      <WorkLine
        tone={quiet ? "quiet" : "done"}
        summary={workSummary(turn, quiet)}
        failed={failed}
        open={open}
        onClick={() => setOpen(!open)}
      />
    </>
  );
  if (wide) {
    return (
      <div data-work={turn.id} className="flex flex-col">
        <div className="flex flex-wrap items-center gap-x-2 pl-10">{line}</div>
        <Collapsible open={open} onOpenChange={setOpen}>
          <CollapsibleContent className={foldMotion}>
            <div className="max-w-[680px] pt-1">
              <WorkSteps threadId={threadId} turnId={turn.id} open={open} />
            </div>
          </CollapsibleContent>
        </Collapsible>
      </div>
    );
  }
  return (
    <div data-work={turn.id} className="flex flex-col pl-10">
      <div className="flex flex-wrap items-center gap-x-2">{line}</div>
      <WorkSheet
        turn={turn}
        threadId={threadId}
        quiet={quiet}
        agentName={agentName}
        time={time}
        open={open}
        onOpenChange={setOpen}
      />
    </div>
  );
}

/** On a phone, the steps in a sheet, titled with the turn's summary (S-509 decision 4). */
function WorkSheet({
  turn,
  threadId,
  quiet,
  agentName,
  time,
  open,
  onOpenChange,
}: {
  turn: Turn;
  threadId: string;
  quiet: boolean;
  agentName: string;
  time: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const worked = workedMs(turn);
  const n = turn.work?.steps ?? 0;
  const failed = turn.work?.failed ?? 0;
  return (
    <Drawer open={open} onOpenChange={onOpenChange} showSwipeHandle>
      <DrawerContent className="pb-[env(safe-area-inset-bottom)]">
        <DrawerHeader className="border-b border-border pb-3.5 group-data-[swipe-axis=y]/drawer-popup:text-left">
          <DrawerTitle className="text-question font-medium">
            {quiet || worked === null ? stepCount(n) : `Worked for ${duration(worked)}`}
          </DrawerTitle>
          <DrawerDescription>
            {[agentName, time, stepCount(n)].join(" · ")}
            {failed > 0 ? (
              <span className="font-medium text-destructive"> · {failed} failed</span>
            ) : null}
          </DrawerDescription>
        </DrawerHeader>
        <div className="px-[18px] pt-1.5 pb-6">
          <WorkSteps threadId={threadId} turnId={turn.id} open={open} />
        </div>
      </DrawerContent>
    </Drawer>
  );
}
